// components/visual/AnimatedFieldBackground.tsx
"use client";

import React, { useEffect, useMemo, useRef } from "react";
import styled, { css, keyframes } from "styled-components";
import * as THREE from "three";
import { Canvas, useFrame, useThree, type RootState } from "@react-three/fiber";
import { useVisualCapabilities } from "./hooks/gradient-particle.";
import SuperpositionParticles from "../Particles/SuperpositionParticles";

// ═══════════════════════════════════════════════════════════════════════════
// SHADER — animated post-quantum "field" background.
// Lifted verbatim out of HeroGradientSection so every section that wants
// this treatment shares one implementation instead of N divergent copies.
// ═══════════════════════════════════════════════════════════════════════════

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  precision highp float;
  varying vec2 vUv;

  uniform float uTime;
  uniform vec2 uMouse;
  uniform vec2 uResolution;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;
  uniform vec3 uColorD;

  vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
  vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}

  float snoise(vec3 v){
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i  = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289(i);
    vec4 p = permute(permute(permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  void main() {
    vec2 uv = vUv;
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 st = (uv - 0.5) * aspect;

    vec2 mouseInfluence = (uMouse - 0.5) * aspect * 0.4;
    float distToMouse = length(st - mouseInfluence);
    float warp = smoothstep(0.6, 0.0, distToMouse) * 0.15;

    float t = uTime * 0.06;
    float n1 = snoise(vec3(st * 1.4 + warp, t));
    float n2 = snoise(vec3(st * 2.2 - t * 0.5, t * 0.7));
    float field = (n1 * 0.65 + n2 * 0.35);
    field = field * 0.5 + 0.5;

    float diag = (uv.x + uv.y) * 0.5;
    float mixVal = clamp(field * 0.7 + diag * 0.3, 0.0, 1.0);

    vec3 col = mix(uColorA, uColorB, smoothstep(0.0, 0.4, mixVal));
    col = mix(col, uColorC, smoothstep(0.35, 0.7, mixVal));
    col = mix(col, uColorD, smoothstep(0.65, 1.0, mixVal));

    float vig = smoothstep(1.1, 0.35, length(st));
    col *= mix(0.85, 1.0, vig);


    gl_FragColor = vec4(pow(col, vec3(1.0 / 2.2)), 1.0);
  }
`;

interface FieldPlaneProps {
  colors: [THREE.Color, THREE.Color, THREE.Color, THREE.Color];
}

const FieldPlane: React.FC<FieldPlaneProps> = ({ colors }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const { size, viewport } = useThree();
  const mouseTarget = useRef(new THREE.Vector2(0.5, 0.5));
  const mouseCurrent = useRef(new THREE.Vector2(0.5, 0.5));

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0.5, 0.5) },
      uResolution: { value: new THREE.Vector2(size.width, size.height) },
      uColorA: { value: colors[0] },
      uColorB: { value: colors[1] },
      uColorC: { value: colors[2] },
      uColorD: { value: colors[3] },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      mouseTarget.current.set(
        e.clientX / window.innerWidth,
        1 - e.clientY / window.innerHeight,
      );
    };
    window.addEventListener("pointermove", handlePointerMove);
    return () => window.removeEventListener("pointermove", handlePointerMove);
  }, []);

  useFrame((state: RootState, delta: number) => {
    if (!materialRef.current) return;
    materialRef.current.uniforms.uTime.value += delta;
    materialRef.current.uniforms.uResolution.value.set(size.width, size.height);
    mouseCurrent.current.lerp(mouseTarget.current, Math.min(delta * 3, 1));
    materialRef.current.uniforms.uMouse.value.copy(mouseCurrent.current);
  });

  return (
    <mesh ref={meshRef} scale={[viewport.width, viewport.height, 1]}>
      <planeGeometry args={[1, 1]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={VERTEX_SHADER}
        fragmentShader={FRAGMENT_SHADER}
        uniforms={uniforms}
      />
    </mesh>
  );
};

const ShaderCanvas: React.FC<{
  colors: [THREE.Color, THREE.Color, THREE.Color, THREE.Color];
}> = ({ colors }) => (
  <Canvas
    dpr={[1, 1.5]}
    gl={{ antialias: false, powerPreference: "low-power" }}
    camera={{ position: [0, 0, 1] }}
    frameloop="always"
  >
    <FieldPlane colors={colors} />
  </Canvas>
);

// ═══════════════════════════════════════════════════════════════════════════
// CSS FALLBACK — base layer; sole layer if WebGL/motion preference disqualifies
// the shader.
// ═══════════════════════════════════════════════════════════════════════════

const cssGradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const CSSFallbackLayer = styled.div<{ $animate: boolean; $colors: string[] }>`
  position: absolute;
  inset: 0;
  background: ${({ $colors }) =>
    `linear-gradient(120deg, ${$colors[0]} 0%, ${$colors[1]} 35%, ${$colors[2]} 65%, ${$colors[3]} 100%)`};
  background-size: 250% 250%;
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${cssGradientShift} 18s ease-in-out infinite;
    `}
`;

export const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

export const ParticleLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
`;

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_COLORS: [string, string, string, string] = [
  "#201202",
  "#bb6707",
  "#3b2307",
  "#351a04",
];

export interface AnimatedFieldBackgroundProps {
  /** Four gradient stops, dark→brand→dark. Defaults to the brand palette. */
  colors?: [string, string, string, string];
  /** Renders the ambient particle layer above the field. Defaults to true. */
  showParticles?: boolean;
}

/**
 * Shared shader + fallback + particle background. Renders as two absolutely
 * positioned layers (z-index 0 and 1) — the parent must be `position:
 * relative` (or similar) and its own content should sit at z-index 2+.
 */
export const AnimatedFieldBackground: React.FC<
  AnimatedFieldBackgroundProps
> = ({ colors = DEFAULT_COLORS, showParticles = true }) => {
  const { reducedMotion, webglOk, checked } = useVisualCapabilities();
  const showShader = checked && webglOk && !reducedMotion;

  const threeColors = useMemo(
    () =>
      colors.map((c) => new THREE.Color(c)) as [
        THREE.Color,
        THREE.Color,
        THREE.Color,
        THREE.Color,
      ],
    [colors],
  );

  return (
    <>
      <BackgroundWrapper>
        <CSSFallbackLayer $animate={!reducedMotion} $colors={colors} />
        {showShader && <ShaderCanvas colors={threeColors} />}
      </BackgroundWrapper>

      {showParticles && !reducedMotion && (
        <ParticleLayer>
          <SuperpositionParticles />
        </ParticleLayer>
      )}
    </>
  );
};

export default AnimatedFieldBackground;
